/**
 * Per-conversation composer draft persistence.
 *
 * Previously the message composer kept a single `draft` string in component
 * state, so an unsent draft leaked into every other conversation. Drafts are
 * now keyed by conversationId and persisted to sessionStorage so reopening a
 * chat restores exactly what was typed there.
 */

function storageKey(conversationId: string): string {
  return `ascend:draft:${conversationId}`
}

/**
 * Writes are debounced: `saveConversationDraft` runs on EVERY keystroke, and a
 * synchronous sessionStorage write per key press measurably contributes to
 * typing jank in the native WebViews. Pending values are read-through, so
 * `getConversationDraft` is always correct even before the write lands.
 */
const WRITE_DEBOUNCE_MS = 400
const pendingWrites = new Map<string, string>()
let writeTimer: ReturnType<typeof setTimeout> | null = null

function flushDraftWrites(): void {
  writeTimer = null
  for (const [conversationId, value] of pendingWrites) {
    try {
      if (value.trim()) {
        sessionStorage.setItem(storageKey(conversationId), value)
      } else {
        sessionStorage.removeItem(storageKey(conversationId))
      }
    } catch {
      /* non-fatal */
    }
  }
  pendingWrites.clear()
}

export function getConversationDraft(conversationId: string | null | undefined): string {
  if (!conversationId) return ''
  const pending = pendingWrites.get(conversationId)
  if (pending !== undefined) return pending
  try {
    return sessionStorage.getItem(storageKey(conversationId)) ?? ''
  } catch {
    return ''
  }
}

export function saveConversationDraft(
  conversationId: string | null | undefined,
  value: string,
): void {
  if (!conversationId) return
  pendingWrites.set(conversationId, value)
  if (writeTimer) clearTimeout(writeTimer)
  writeTimer = setTimeout(flushDraftWrites, WRITE_DEBOUNCE_MS)
}

/**
 * Prefill a composer draft without wiping unsent text the user already had.
 * Empty composer → the prefill; otherwise append (once) under the existing
 * text. Used by "Send message" on the recording-consent peer notice.
 */
export function saveConversationDraftPrefill(
  conversationId: string | null | undefined,
  prefill: string,
): void {
  if (!conversationId || !prefill.trim()) return
  const existing = getConversationDraft(conversationId).trim()
  if (!existing) {
    saveConversationDraft(conversationId, prefill)
    return
  }
  if (existing.includes(prefill.trim())) return
  saveConversationDraft(conversationId, `${existing}\n\n${prefill}`)
}

export function clearConversationDraft(conversationId: string | null | undefined): void {
  if (!conversationId) return
  pendingWrites.delete(conversationId)
  try {
    sessionStorage.removeItem(storageKey(conversationId))
  } catch {
    /* non-fatal */
  }
}

const DRAFT_KEY_PREFIX = 'ascend:draft:'

/**
 * Purge every draft on sign-out. Drafts are unsent message text — session
 * teardown must not leave them readable to the next account on this device,
 * and the debounce timer must be cancelled so an in-flight write cannot
 * re-persist a draft AFTER the purge ran.
 */
export function clearAllConversationDrafts(): void {
  if (writeTimer) {
    clearTimeout(writeTimer)
    writeTimer = null
  }
  pendingWrites.clear()
  try {
    const stale: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(DRAFT_KEY_PREFIX)) stale.push(key)
    }
    for (const key of stale) sessionStorage.removeItem(key)
  } catch {
    /* non-fatal */
  }
}
